import type { InvEditRecord, InvAuditEntry, InvEditChange } from './types';
import { t } from '../lib/i18n';

/**
 * JCD-56 inventory edit mode.
 *
 * Mirrors stockAdjust.ts for the write: read the `pacebi_csrf` cookie, echo
 * it via `X-CSRF-Token`, and mint a fresh idempotency key per call. That key
 * dedupes an identical RETRANSMISSION — a network-level retry of a request
 * whose response was lost — not a double-click, since each click mints its
 * own key. The double-click guard is the form's in-flight `saving` state.
 */

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

export async function fetchInvEditRecord(sid: number, line: string, part: string): Promise<InvEditRecord> {
  const q = new URLSearchParams({ sid: String(sid), line, part });
  const res = await fetch(`/api/inv-edit-record?${q}`, { credentials: 'same-origin' });
  // 404 means the part simply has no inv row in this store — an ordinary
  // state, since the store filter picks the store. Say that in words rather
  // than passing the daemon's own "part not found" through to the panel.
  if (res.status === 404) throw new Error(t.part.edit.notStocked);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text.trim() || `HTTP ${res.status}`);
  }
  return (await res.json()) as InvEditRecord;
}

/** Applies only the keys present in `changes` — the server writes nothing
 *  else, and rejects any key that is not an editable field.
 *
 *  `curPack` is the pack this form loaded with, so the server can validate
 *  ordpack against it exactly as partfm.p:1053 validates against INPUT pack. */
export async function postInvEdit(
  sid: number,
  line: string,
  part: string,
  changes: Record<string, number | string>,
  curPack: number,
): Promise<{ sid: number; changed: number; changes: InvEditChange[] }> {
  const csrf = readCookie('pacebi_csrf');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch('/api/inv-edit', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({
      sid,
      line,
      part,
      changes,
      cur_pack: curPack,
      idempotency_key: crypto.randomUUID(),
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text.trim() || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function fetchInvAudit(
  sid: number,
  line: string,
  part: string,
  limit = 50,
): Promise<InvAuditEntry[]> {
  const q = new URLSearchParams({ sid: String(sid), line, part, limit: String(limit) });
  const res = await fetch(`/api/inv-audit?${q}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const j = await res.json();
  return (j.entries ?? []) as InvAuditEntry[];
}
